// HDR photos -> a normal (SDR, sRGB) picture, before the crop (crop.js).
//
// iPhones and macOS 26 export "HDR" JPEGs whose ICC profile is not a normal colour profile but a
// PQ (or HLG) transfer curve as a v4 LUT profile ("Display P3 Primaries; PQ (Adaptive Gain Curve
// ...)"). Chrome draws those flat and dark grey, and a canvas crop bakes that in (Dennis' photo,
// 2026-10-13). So: read the profile from the JPEG's APP2 segments; if it is PQ/HLG, decode the
// pixels WITHOUT colour conversion and do it ourselves:
//   code value -> PQ/HLG EOTF -> nits -> /203 (HDR reference white = SDR white) -> soft shoulder
//   -> P3/BT.2020 primaries to sRGB -> sRGB curve.
// Everything else (sRGB, plain Display P3, PNG, WebP) is left to the browser.
//
//   const blob = await toSdr(file)    // a JPEG Blob, or null when the file needs no help

const MAX_SIDE = 2400;          // more than the crop ever needs (it writes 1024 at most)
const REFERENCE_WHITE = 203;    // nits; ITU-R BT.2408: HDR reference white = diffuse white in SDR
const KNEE = 0.8;               // below this (relative to white) the picture stays linear

// --- the ICC profile in a JPEG -------------------------------------------------------------
function jpegIcc(bytes) {
  if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null;
  const chunks = [];
  let i = 2;
  while (i + 4 <= bytes.length && bytes[i] === 0xFF) {
    const marker = bytes[i + 1];
    if (marker === 0xDA || marker === 0xD9) break;                 // start of scan: no more headers
    if (marker === 0xFF) { i += 1; continue; }                     // fill byte
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    // APP2 "ICC_PROFILE\0" + sequence number + count + data
    if (marker === 0xE2 && len > 16 && String.fromCharCode(...bytes.subarray(i + 4, i + 15)) === 'ICC_PROFILE') {
      chunks.push({seq: bytes[i + 16], data: bytes.subarray(i + 18, i + 2 + len)});
    }
    i += 2 + len;
  }
  if (!chunks.length) return null;
  chunks.sort((a, b) => a.seq - b.seq);
  const out = new Uint8Array(chunks.reduce((n, c) => n + c.data.length, 0));
  let at = 0;
  chunks.forEach(c => { out.set(c.data, at); at += c.data.length; });
  return out;
}

const u32 = (b, o) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;

function iccTag(icc, sig) {
  if (icc.length < 132) return null;
  const n = u32(icc, 128);
  for (let t = 0; t < n && 132 + 12 * t + 12 <= icc.length; t++) {
    const o = 132 + 12 * t;
    if (String.fromCharCode(...icc.subarray(o, o + 4)) === sig) {
      const off = u32(icc, o + 4);
      const len = u32(icc, o + 8);
      return off + len <= icc.length ? icc.subarray(off, off + len) : null;
    }
  }
  return null;
}

function iccDescription(icc) {
  const tag = iccTag(icc, 'desc');
  if (!tag) return '';
  const type = String.fromCharCode(...tag.subarray(0, 4));
  if (type === 'desc') {                                   // v2: ASCII
    const count = u32(tag, 8);
    return String.fromCharCode(...tag.subarray(12, 12 + Math.max(0, count - 1)));
  }
  if (type === 'mluc' && tag.length >= 28) {               // v4: the first record, UTF-16BE
    const len = u32(tag, 20);
    const off = u32(tag, 24);
    let s = '';
    for (let k = off; k + 1 < off + len && k + 1 < tag.length; k += 2) s += String.fromCharCode((tag[k] << 8) | tag[k + 1]);
    return s;
  }
  return '';
}

// {transfer: 'pq'|'hlg', primaries: 'p3'|'bt2020'|'srgb'} for an HDR profile, otherwise null
function hdrProfile(icc) {
  if (!icc) return null;
  let transfer = null;
  let primaries = null;
  const cicp = iccTag(icc, 'cicp');                         // ICC v4.4: the code points, if present
  if (cicp && cicp.length >= 12) {
    transfer = {16: 'pq', 18: 'hlg'}[cicp[9]] || null;
    primaries = {1: 'srgb', 9: 'bt2020', 12: 'p3'}[cicp[8]] || null;
  }
  const desc = iccDescription(icc);
  if (!transfer) {
    if (/\bPQ\b|ST[ .]?2084|SMPTE ?2084/i.test(desc)) transfer = 'pq';
    else if (/\bHLG\b|ARIB|Hybrid Log/i.test(desc)) transfer = 'hlg';
  }
  if (!transfer) return null;
  if (!primaries) primaries = /2020|2100/.test(desc) ? 'bt2020' : (/709|sRGB/i.test(desc) ? 'srgb' : 'p3');
  return {transfer, primaries, description: desc};
}

// --- the conversion ------------------------------------------------------------------------
const TO_SRGB = {        // linear RGB of the primaries -> linear sRGB (D65 everywhere)
  p3: [1.2249401, -0.2249404, 0, -0.0420569, 1.0420571, 0, -0.0196376, -0.0786361, 1.0982735],
  bt2020: [1.6604910, -0.5876411, -0.0728499, -0.1245505, 1.1328999, -0.0083494, -0.0181508, -0.1005789, 1.1187297],
  srgb: [1, 0, 0, 0, 1, 0, 0, 0, 1],
};
const LUMA = {p3: [0.2289746, 0.6917385, 0.0792869], bt2020: [0.2627, 0.6780, 0.0593], srgb: [0.2126, 0.7152, 0.0722]};

function pqToNits(v) {
  const m1 = 2610 / 16384, m2 = 2523 / 4096 * 128, c1 = 3424 / 4096, c2 = 2413 / 4096 * 32, c3 = 2392 / 4096 * 32;
  const p = Math.pow(v, 1 / m2);
  return 10000 * Math.pow(Math.max(p - c1, 0) / (c2 - c3 * p), 1 / m1);
}
function hlgToScene(v) {        // HLG inverse OETF -> scene light 0..1
  const a = 0.17883277, b = 0.28466892, c = 0.55991073;
  return v <= 0.5 ? (v * v) / 3 : (Math.exp((v - c) / a) + b) / 12;
}
// a soft shoulder: linear up to KNEE, then highlights roll off towards 1 (never clip hard)
function shoulder(y) {
  if (y <= KNEE) return y;
  const room = 1 - KNEE;
  return KNEE + room * (1 - Math.exp(-(y - KNEE) / room));
}

function convert(data, profile) {
  // code value (0..255) -> linear light relative to reference white
  const lin = new Float32Array(256);
  for (let v = 0; v < 256; v++) {
    lin[v] = profile.transfer === 'pq' ? pqToNits(v / 255) / REFERENCE_WHITE : hlgToScene(v / 255);
  }
  const hlg = profile.transfer === 'hlg';
  const [wr, wg, wb] = LUMA[profile.primaries];
  const m = TO_SRGB[profile.primaries];
  const SIZE = 4096;                                       // linear 0..1 -> sRGB 8 bit
  const out = new Uint8ClampedArray(SIZE + 1);
  for (let k = 0; k <= SIZE; k++) {
    const x = k / SIZE;
    out[k] = Math.round(255 * (x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055));
  }
  for (let i = 0; i < data.length; i += 4) {
    let r = lin[data[i]], g = lin[data[i + 1]], b = lin[data[i + 2]];
    let y = wr * r + wg * g + wb * b;
    if (hlg) {                                             // HLG OOTF for a 1000-nit display, gamma 1.2
      const k = 1000 * Math.pow(Math.max(y, 1e-6), 0.2) / REFERENCE_WHITE;
      r *= k; g *= k; b *= k; y *= k;
    }
    if (y > KNEE) { const s = shoulder(y) / y; r *= s; g *= s; b *= s; }
    let R = m[0] * r + m[1] * g + m[2] * b;
    let G = m[3] * r + m[4] * g + m[5] * b;
    let B = m[6] * r + m[7] * g + m[8] * b;
    // out of gamut / still above white: keep the hue, scale down the brightest channel to 1
    const top = Math.max(R, G, B);
    if (top > 1) { R /= top; G /= top; B /= top; }
    data[i] = out[Math.round(Math.max(0, R) * SIZE)];
    data[i + 1] = out[Math.round(Math.max(0, G) * SIZE)];
    data[i + 2] = out[Math.round(Math.max(0, B) * SIZE)];
  }
}

async function toSdr(file) {
  try {
    if (!window.createImageBitmap) return null;
    // the profile sits in the first headers; 256 KB is plenty (26 KB on Dennis' photo)
    const head = new Uint8Array(await file.slice(0, 262144).arrayBuffer());
    const profile = hdrProfile(jpegIcc(head));
    if (!profile) return null;
    const probe = await createImageBitmap(file, {colorSpaceConversion: 'none', imageOrientation: 'from-image'});
    const scale = Math.min(1, MAX_SIDE / Math.max(probe.width, probe.height));
    const width = Math.round(probe.width * scale), height = Math.round(probe.height * scale);
    let bitmap = probe;
    if (scale < 1) {
      probe.close();
      bitmap = await createImageBitmap(file, {colorSpaceConversion: 'none', imageOrientation: 'from-image', resizeWidth: width, resizeHeight: height, resizeQuality: 'high'});
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const pixels = ctx.getImageData(0, 0, width, height);
    convert(pixels.data, profile);
    ctx.putImageData(pixels, 0, 0);
    return await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  } catch (e) {
    console.warn('HDR conversion failed, using the photo as it is', e);
    return null;
  }
}

module.exports = {toSdr, hdrProfile, jpegIcc};
